/**
 * Concurrency primitives for admin import + bulk-action flows.
 *
 * Extracted from `BulkInvitationImportDialog` so the same concurrency
 * runner can serve the upcoming user-import (Wave 5) and the bulk
 * email-send action (Wave 4) without three drift-prone copies.
 */

/**
 * Run `worker` over `items` with at most `limit` tasks in flight at once.
 *
 * Preserves the original order in the returned array. Each "lane" awaits
 * its current task before starting the next item, so the total in-flight
 * work never exceeds `limit`. If `items` is empty or `limit <= 0`, returns
 * an empty array.
 *
 * Errors thrown inside `worker` propagate from the lane and reject the
 * top-level Promise — callers that want per-item error tolerance should
 * wrap the worker body in a try/catch.
 */
export async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  if (items.length === 0 || limit <= 0) return results;
  let cursor = 0;

  async function runLane(): Promise<void> {
    while (true) {
      const index = cursor++;
      if (index >= items.length) return;
      results[index] = await worker(items[index], index);
    }
  }

  const lanes = Array.from(
    { length: Math.min(limit, items.length) },
    () => runLane(),
  );
  await Promise.all(lanes);
  return results;
}
